'use client';

import { createContext, useContext, useState, useEffect, ReactNode, useRef, useCallback } from 'react';

interface CartItem {
  _id: string;
  productId: string;
  productName: string;
  productSlug: string;
  image: string;
  quantity: number;
  price: number;
  subtotal: number;
  stockAvailable: number | null;
  trackStock: boolean;
  isAvailable: boolean;
  selectedAttributes?: Record<string, string>;
  addedAt: Date;
}

interface CartContextType {
  items: CartItem[];
  itemCount: number;
  subtotal: number;
  total: number;
  cartId: string;
  isLoading: boolean;
  isRefreshing: boolean;
  error: string | null;
  addToCart: (data: {
    productId: string;
    quantity: number;
    selectedAttributes?: Record<string, string>;
    productData?: any;
  }) => Promise<void>;
  updateQuantity: (itemId: string, quantity: number) => Promise<void>;
  removeItem: (itemId: string) => Promise<void>;
  clearCart: () => Promise<void>;
  refreshCart: (force?: boolean) => Promise<void>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const getApiUrl = () => {
  return process.env.NEXT_PUBLIC_API_URL || 'http://localhost:5000';
};

async function fetchCart(endpoint: string, options: RequestInit = {}) {
  const response = await fetch(`${getApiUrl()}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    credentials: 'include'
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return await response.json();
}

function calculateTotals(items: CartItem[]) {
  const subtotal = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
  return { subtotal, itemCount, total: subtotal };
}

export function CartProvider({ children }: { children: ReactNode }) {

  const [items, setItems] = useState<CartItem[]>([]);
  const [itemCount, setItemCount] = useState(0);
  const [subtotal, setSubtotal] = useState(0);
  const [total, setTotal] = useState(0);
  const [cartId, setCartId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const lastFetch = useRef<number>(0);
  const fetchInProgress = useRef<boolean>(false);
  const isMounted = useRef<boolean>(true);
  const itemOperations = useRef<Set<string>>(new Set());

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Initial cart load
  useEffect(() => {
    refreshCart();
  }, []);

  const refreshCart = useCallback(async (force = false) => {

    const timeSinceLastFetch = Date.now() - lastFetch.current;
    
    if (!force && timeSinceLastFetch < 1000) {
      console.log('Skipping duplicate cart fetch');
      return;
    }

    if (fetchInProgress.current) {
      console.log('Fetch already in progress');
      return;
    }

    try {

      fetchInProgress.current = true;
      setIsRefreshing(true);
      setError(null);
      lastFetch.current = Date.now();

      // console.log('Refreshing cart...');
      const data = await fetchCart('/api/cart');
      // console.log("[CartContext] GET /api/cart raw:", JSON.stringify(data, null, 2));

      if (!isMounted.current) return;

      const responseCartId = data.data?._id || data._id || data.cartId || `cart-${Date.now()}`;
      const responseItems = data.items || data.data?.items || [];

      setCartId(responseCartId);
      setItems(responseItems);
      setItemCount(data.itemCount || data.data?.itemCount || 0);
      setSubtotal(data.subtotal || data.data?.subtotal || 0);
      setTotal(data.total || data.data?.total || 0);
      
      // console.log('✅ Cart refreshed:', {
      //   cartId: responseCartId,
      //   itemCount: data.itemCount || 0
      // });

    } catch (err) {
      console.error('❌ Failed to fetch cart:', err);
      if (isMounted.current) {
        setError(err instanceof Error ? err.message : 'Failed to load cart');
        setItems([]);
        setItemCount(0);
        setSubtotal(0);
        setTotal(0);
        setCartId('');
      }
    } finally {
      if (isMounted.current) {
        setIsRefreshing(false);
        setIsLoading(false);
      }
      fetchInProgress.current = false;
    }
  }, []);

  // ✅ CRITICAL FIX: Listen for login events
  useEffect(() => {
    const handleLogin = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { cartMerged, wishlistMerged } = customEvent.detail || {};
      
      console.log('🔔 Login event detected - refreshing cart');
      console.log('   Cart merged:', cartMerged);
      console.log('   Wishlist merged:', wishlistMerged);
      
      // Force refresh to get merged cart
      setTimeout(() => refreshCart(true), 500);
    };

    window.addEventListener('userLogin', handleLogin);
    return () => window.removeEventListener('userLogin', handleLogin);
  }, [refreshCart]);

  // Listen for logout events
  useEffect(() => {
    const handleLogout = () => {
      console.log('🔔 Logout event - refreshing cart');
      setItems([]);
      setItemCount(0);
      setSubtotal(0);
      setTotal(0);
      setCartId('');
      setError(null);
      // Refresh to get guest cart
      setTimeout(() => refreshCart(true), 500);
    };

    window.addEventListener('userLogout', handleLogout);
    return () => window.removeEventListener('userLogout', handleLogout);
  }, [refreshCart]);

  // Listen for order completion
  useEffect(() => {
    const handleOrderComplete = () => {
      console.log('🔔 Order completed - clearing cart');
      setItems([]);
      setItemCount(0);
      setSubtotal(0);
      setTotal(0);
      setTimeout(() => refreshCart(true), 500);
    };

    window.addEventListener('orderCompleted', handleOrderComplete);
    return () => window.removeEventListener('orderCompleted', handleOrderComplete);
  }, [refreshCart]);

  const addToCart = async (data: {
    productId: string;
    quantity: number;
    selectedAttributes?: Record<string, string>;
    productData?: any;
  }) => {
    try {
      setError(null);

      const response = await fetchCart('/api/cart/add', {
        method: 'POST',
        body: JSON.stringify({
          productId: data.productId,
          quantity: data.quantity,
          selectedAttributes: data.selectedAttributes
        })
      });

      if (!isMounted.current) return response;

      setItemCount(response.cartItemCount || 0);
      await refreshCart(true);

      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add to cart';
      if (isMounted.current) {
        setError(errorMessage);
      }
      throw new Error(errorMessage);
    }
  };

  const updateQuantity = async (itemId: string, quantity: number) => {
    if (itemOperations.current.has(itemId)) {
      console.log('⏭️ Update already in progress for item:', itemId);
      return;
    }

    const originalItems = [...items];
    const originalSubtotal = subtotal;
    const originalItemCount = itemCount;
    const originalTotal = total;

    try {
      itemOperations.current.add(itemId);

      // Optimistic update
      const updatedItems = items.map(item => {
        if (item._id === itemId) {
          return {
            ...item,
            quantity,
            subtotal: item.price * quantity
          };
        }
        return item;
      });

      setItems(updatedItems);
      const { subtotal: newSubtotal, itemCount: newItemCount, total: newTotal } = 
        calculateTotals(updatedItems);
      
      setSubtotal(newSubtotal);
      setItemCount(newItemCount);
      setTotal(newTotal);

      await fetchCart(`/api/cart/update/${itemId}`, {
        method: 'PUT',
        body: JSON.stringify({ quantity })
      });

      setTimeout(() => refreshCart(true).catch(console.error), 500);

    } catch (err) {
      if (isMounted.current) {
        setItems(originalItems);
        setSubtotal(originalSubtotal);
        setItemCount(originalItemCount);
        setTotal(originalTotal);

        const errorMessage = err instanceof Error ? err.message : 'Failed to update quantity';
        setError(errorMessage);
      }
      throw err;
    } finally {
      itemOperations.current.delete(itemId);
    }
  };

  const removeItem = async (itemId: string) => {
    if (itemOperations.current.has(itemId)) {
      console.log('⏭️ Remove already in progress for item:', itemId);
      return;
    }

    console.log('🗑️ Removing item:', itemId);
    
    try {
      itemOperations.current.add(itemId);

      const freshCartData = await fetchCart('/api/cart');
      const freshItems = freshCartData.items || freshCartData.data?.items || [];
      
      const itemExists = freshItems.some((item: CartItem) => item._id === itemId);
      
      if (!itemExists) {
        console.error('❌ Item not found in cart');
        if (isMounted.current) {
          setItems(freshItems);
          const { subtotal: newSub, itemCount: newCount, total: newTotal } = 
            calculateTotals(freshItems);
          setSubtotal(newSub);
          setItemCount(newCount);
          setTotal(newTotal);
          setError('Item not found in cart');
        }
        return;
      }
      
      const updatedItems = freshItems.filter((item: CartItem) => item._id !== itemId);
      
      if (isMounted.current) {
        setItems(updatedItems);
        
        const { subtotal: newSubtotal, itemCount: newItemCount, total: newTotal } = 
          calculateTotals(updatedItems);
        
        setSubtotal(newSubtotal);
        setItemCount(newItemCount);
        setTotal(newTotal);
      }
      
      await fetchCart(`/api/cart/remove/${itemId}`, {
        method: 'DELETE'
      });
      
      setTimeout(() => refreshCart(true).catch(console.error), 800);
      
    } catch (err) {
      console.error('❌ Remove item error:', err);
      
      await refreshCart(true);
      
      if (isMounted.current) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to remove item';
        setError(errorMessage);
      }
      throw err;
    } finally {
      itemOperations.current.delete(itemId);
    }
  };

  const clearCart = async () => {
    try {
      setError(null);

      await fetchCart('/api/cart/clear', {
        method: 'DELETE'
      });

      if (isMounted.current) {
        setItems([]);
        setItemCount(0);
        setSubtotal(0);
        setTotal(0);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to clear cart';
      if (isMounted.current) {
        setError(errorMessage);
      }
      throw new Error(errorMessage);
    }
  };

  return (
    <CartContext.Provider
      value={{
        items,
        itemCount,
        subtotal,
        total,
        cartId,
        isLoading,
        isRefreshing,
        error,
        addToCart,
        updateQuantity,
        removeItem,
        clearCart,
        refreshCart
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  
  return context;
}